import { EffectType, VitalType } from "@1games-ru/region-shared/dist/enums/effect/effect";
import { isNumber } from "lodash";
import { FC, useEffect, useState } from "react";
import { useTimer } from "react-timer-hook";
import clsx from "clsx";
import {
  BicepsIcon,
  DropPulseIcon,
  EasyHungryIcon,
  HardHungryIcon,
  HeartPulseIcon,
  MedPatchIcon,
  MedPillIcon,
  PizzaIcon,
  WineIcon,
  ZapIcon,
} from "@/shared/icons";
import { BaseIconProps } from "@/shared/icons/BaseIcon";
import { percentByPeriod } from "@/domains/hud/helpers/percentByPeriod";
import InventoryCharacterProgressIcon from "@/domains/inventory/components/character/InventoryCharacterProgressIcon";

type Props = {
  type: VitalType | EffectType;
  className?: string;
  label?: string;
  startedAt?: number;
  finishedAt?: number;
  percent?: number;
  onlyIcon?: boolean;
};

const icons: Partial<Record<VitalType | EffectType, FC<BaseIconProps>>> = {
  [VitalType.Health]: HeartPulseIcon,
  [VitalType.Satiety]: PizzaIcon,
  // [""]: SmileIcon,
  // [""]: PillsIcon,
  // [""]: DropletsIcon,
  // [""]: MedPatchIcon,
  [EffectType.Bleeding]: DropPulseIcon,
  [EffectType.Injury]: MedPatchIcon,
  [EffectType.Regeneration]: MedPillIcon,
  [EffectType.WeakHunger]: EasyHungryIcon,
  [EffectType.StrongHunger]: HardHungryIcon,
  [EffectType.EnergyBoost]: ZapIcon,
  [EffectType.WellFed]: BicepsIcon,
  [EffectType.Drunk]: WineIcon,
};

export default function InventoryCharacterEffectType({
  type,
  startedAt,
  finishedAt,
  percent,
  label,
  className,
  onlyIcon,
}: Props) {
  const hasFinish = isNumber(finishedAt);
  const { seconds, minutes } = useTimer({
    expiryTimestamp: hasFinish ? new Date(finishedAt * 1000) : new Date(),
    autoStart: hasFinish,
  });
  const [timer, setTimer] = useState<string>("");
  const [progress, setProgress] = useState<number | undefined>();

  useEffect(() => {
    if (startedAt && finishedAt) {
      setTimer(`${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`);
      setProgress(percentByPeriod(startedAt * 1000, finishedAt * 1000));
    } else {
      setTimer("");
      setProgress(undefined);
    }
  }, [startedAt, finishedAt, minutes, seconds]);

  const icon = (
    <InventoryCharacterProgressIcon
      progress={percent ?? progress ?? 100}
      size={20}
      icon={icons[type]}
      className={onlyIcon ? className : ""}
    />
  );

  if (onlyIcon) return icon;

  return (
    <div className={clsx("flex items-center gap-2 py-2.5 px-3.5", className)}>
      <InventoryCharacterProgressIcon
        progress={percent ?? progress ?? 100}
        size={20}
        icon={icons[type]}
      />
      <div className="text-11 font-bold uppercase text-white">{label || "-"}</div>
      {timer && <div className="text-13 font-bold uppercase ml-auto tabular-nums">{timer}</div>}
      {isNumber(percent) && (
        <div className="text-13 font-bold uppercase ml-auto  tabular-nums">{percent}%</div>
      )}
    </div>
  );
}
